import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { BoundedName, BoundedNameDTO } from "../types/boundedName";
import { TemplateContent, TemplateContentDTO } from "./templateContent";
/* eslint-disable import/export */
export abstract class UpdateEmailTemplateRPC implements
  LeoRPC<UpdateEmailTemplateRPC.Request, UpdateEmailTemplateRPC.Response, UpdateEmailTemplateRPC.Errors.Errors>
{
  abstract execute(
    request: UpdateEmailTemplateRPC.Request
  ): Promise<LeoRPCResult<UpdateEmailTemplateRPC.Response, UpdateEmailTemplateRPC.Errors.Errors>>;
}

export namespace UpdateEmailTemplateRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    templateId: string;
    templateName: BoundedNameDTO;
    templateContent: TemplateContentDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isUpdateEmailTemplateRPCRequest: boolean = true;
  
    readonly templateId: LeoUUID;
    readonly templateName: BoundedName;
    readonly templateContent: TemplateContent;
  
  
    constructor(
    templateId: LeoUUID, 
    templateName: BoundedName, 
    templateContent: TemplateContent
    ) {
      this.templateId = templateId;
      this.templateName = templateName;
      this.templateContent = templateContent;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const templateId = getLeoUuid(dto, "templateId", InvalidRequestTemplateIdError);
      const templateName = BoundedName.fromDTO(getObject(dto, "templateName", InvalidRequestTemplateNameError));
      const templateContent = TemplateContent.fromDTO(getObject(dto, "templateContent", InvalidRequestTemplateContentError));
      return new Request(
        templateId,
        templateName,
        templateContent
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          templateId: this.templateId.toDTO(),
          templateName: this.templateName.toDTO(),
          templateContent: this.templateContent.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `UpdateEmailTemplateRPC(templateId='${this.templateId}',templateName='${this.templateName}',templateContent='${this.templateContent}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `UpdateEmailTemplateRPC.Response()`;
  };

  export enum RPCError {
    DuplicateTemplateName = "DUPLICATE_TEMPLATE_NAME",
    InvalidTemplateId = "INVALID_TEMPLATE_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED"
  }

  export namespace Errors {
    
    export interface DuplicateTemplateNameDTO {
      code: string;
    }
    
    export class DuplicateTemplateName extends LeoRPCError<RPCError> {
      readonly isDuplicateTemplateName: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DuplicateTemplateName;
        super(code);
      }
    
    
      toDTO(): DuplicateTemplateNameDTO {
        const dto: DuplicateTemplateNameDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DuplicateTemplateName.prototype.toString = function toString(): string {
      return `UpdateEmailTemplateRPC.DUPLICATE_TEMPLATE_NAME()`;
    };
    
    
    export interface InvalidTemplateIdDTO {
      code: string;
    }
    
    export class InvalidTemplateId extends LeoRPCError<RPCError> {
      readonly isInvalidTemplateId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidTemplateId;
        super(code);
      }
    
    
      toDTO(): InvalidTemplateIdDTO {
        const dto: InvalidTemplateIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidTemplateId.prototype.toString = function toString(): string {
      return `UpdateEmailTemplateRPC.INVALID_TEMPLATE_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `UpdateEmailTemplateRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    

    export type Errors = DuplicateTemplateName | InvalidTemplateId | ProjectAlreadyArchived;
    export type ErrorsDTO = DuplicateTemplateNameDTO | InvalidTemplateIdDTO | ProjectAlreadyArchivedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.DuplicateTemplateName:
          return new DuplicateTemplateName();
        case RPCError.InvalidTemplateId:
          return new InvalidTemplateId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestTemplateIdError extends UpdateEmailTemplateRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestTemplateIdError";
  }
}

export class InvalidRequestTemplateNameError extends UpdateEmailTemplateRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestTemplateNameError";
  }
}

export class InvalidRequestTemplateContentError extends UpdateEmailTemplateRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestTemplateContentError";
  }
}


export class InvalidResponseDuplicateTemplateNameError extends UpdateEmailTemplateRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDuplicateTemplateNameError";
  }
}

export class InvalidResponseInvalidTemplateIdError extends UpdateEmailTemplateRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidTemplateIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends UpdateEmailTemplateRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

